import { useEffect, useRef } from 'react'
import { documentHasContent, draftDocument } from 'helpers/richTextDocument'

// A text block (ADR 0013): written once in its original language, translated by the API into the platform's other languages.
export const TEXT_LANGUAGES = [['ro', 'Romanian'], ['en', 'English'], ['bg', 'Bulgarian']]
export const languageName = code => TEXT_LANGUAGES.find(([key]) => key === code)?.[1] || code
export const targetLanguages = original => TEXT_LANGUAGES.map(([key]) => key).filter(key => key !== original)
// The translation chain (ADR 0013): its attempts on the main model, then as many on the backup, and the wait between two.
export const TRANSLATION_CHAIN = { attemptsPerModel: 3, retrySeconds: 10 }

// The language a new text starts in: the country's, when the platform has it; English otherwise.
const COUNTRY_LANGUAGES = { Romania: 'ro', Moldova: 'ro', Bulgaria: 'bg' }
export const countryLanguage = country => COUNTRY_LANGUAGES[country] || 'en'

// Whether any of a record's blocks (`localizedTexts`, by name, as a read gives them) is being translated.
export const anyPending = localizedTexts => Object.values(localizedTexts || {}).some(block => block?.state === 'pending')
// Whether any of a list row's blocks is being translated, as the API marks them (`translationAttention`, each block's state by its name).
export const anyTranslating = translationAttention => Object.values(translationAttention || {}).includes('pending')
// What a list marks of a block as a read gives it (ADR 0013), the API's rule for `translationAttention`: `pending` while it translates,
// `languageMismatch` while its language waits for its author, `blocked` while a language has no current variant after the chain gave
// up; otherwise nothing (null).
export const blockAttention = block => {
  if (['pending', 'languageMismatch'].includes(block?.state)) return block.state
  if (block?.state === 'blocked' && TEXT_LANGUAGES.some(([language]) => !block.variants?.[language]?.current)) return 'blocked'
  return null
}

// While a block's translation is in progress, `refresh` reads it again every 3 seconds. The chain lasts at most 230 seconds, so the
// refresh gives up after five minutes; a new `restartKey` (the save that started another translation) gives it five more.
const REFRESH_MS = 3000
const REFRESH_LIMIT_MS = 300000
export function useTranslationRefresh(pending, refresh, restartKey) {
  const latest = useRef(refresh)
  latest.current = refresh
  useEffect(() => {
    if (!pending) return undefined
    const deadline = Date.now() + REFRESH_LIMIT_MS
    let timer
    let stopped = false
    const tick = async () => {
      try { await latest.current() } catch { /* The next refresh tries again. */ }
      if (!stopped && Date.now() < deadline) timer = setTimeout(tick, REFRESH_MS)
    }
    timer = setTimeout(tick, REFRESH_MS)
    return () => { stopped = true; clearTimeout(timer) }
  }, [pending, restartKey])
}

// Each save held back for a translation typed and not saved (`heldSaves` counts them) brings the author to it: the section of the zone
// `zoneId` in `element` opens, and the zone takes the focus.
export function useHeldSaveFocus(element, zoneId, heldSaves) {
  useEffect(() => {
    if (!heldSaves) return
    const zone = element.current?.querySelector('#' + zoneId)
    const section = zone?.closest('details')
    if (section) section.open = true
    zone?.focus()
  }, [element, zoneId, heldSaves])
}

// The texts of a content, however deep: a player package's fields, each place of the prizes, or the lines of a rich text.
const textsOf = content => typeof content === 'string' ? draftDocument(content)?.blocks.map(line => line.text) ?? [content]
  : content && typeof content === 'object' ? Object.values(content).flatMap(textsOf) : []
export const hasText = content => textsOf(content).some(text => text.trim() !== '')
// A rich text an editor left empty, a document that shows nothing, is the same text as none.
export function isEmptyRichText(text) {
  const document = draftDocument(text)
  return Boolean(document) && !documentHasContent(document)
}
export const sameText = (text, other) => text === other || [text, other].every(value => !value || isEmptyRichText(value))

// The languages of a block whose translation its author typed by hand (`drafts`, by language) and has not saved yet. A language whose
// variant is current has nothing left to save, and once the original changes (`originalChanged`) none counts: the next translation
// follows the new original.
export const typedTranslations = (drafts, block, originalChanged) => originalChanged ? []
  : Object.keys(drafts).filter(language => hasText(drafts[language]) && !block?.variants?.[language]?.current)
// Whether a block, as a read gives it, shows an author's command: the language they confirmed, or the translation they wrote by hand.
export const showsCommand = ({ action, language }, block) => action === 'confirmLanguage'
  ? Boolean(block) && block.state !== 'languageMismatch' && block.originalLanguage === language
  : Boolean(block?.variants?.[language]?.current) && block.variants[language].origin === 'manual'

// Why the save of what holds the block (`subject`: "event", "competition") waits for those translations.
export const heldSaveMessage = (languages, subject) => 'Save the ' + languages.map(languageName).join(' and ')
  + ' manual translation with Save translation, or clear it, before saving the ' + subject + '.'

// Bulgarian is written in Cyrillic, Romanian and English in Latin letters. A text long enough to tell that uses the other alphabet
// reads 'cyrillic' or 'latin'; brand names in Latin letters inside a Bulgarian text do not.
export function scriptMismatch(content, language) {
  const letters = textsOf(content).join(' ').match(/\p{L}/gu) || []
  if (letters.length < 12) return null
  const cyrillic = letters.filter(letter => /\p{Script=Cyrillic}/u.test(letter)).length / letters.length
  if (language === 'bg' && cyrillic < 0.3) return 'latin'
  if (language !== 'bg' && cyrillic > 0.7) return 'cyrillic'
  return null
}
